import { americanToImplied, impliedToAmerican } from './liveOddsVnextModel.js'

export const FAIR_VALUE_METHODS = Object.freeze({
  multiplicative: 'Multiplicative no-vig',
  additive: 'Additive no-vig',
})

export const CONSENSUS_WEIGHTING = Object.freeze({
  equal: 'Equal book weight',
  market_quality: 'Market-quality weight',
})

// Transparent defaults, not an assertion that any source is always sharp.
export const MARKET_QUALITY_WEIGHTS = Object.freeze({
  pinnacle: 1.5,
  prophetx: 1.35,
  matchbook: 1.25,
  novig: 1.2,
  betopenly: 1.15,
  smarkets: 1.15,
  sporttrade: 1.15,
})

function finiteProbability(row) {
  const value = americanToImplied(row?.american)
  return Number.isFinite(value) && value > 0 && value < 1 ? value : null
}

function normalize(values) {
  const total = values.reduce((sum, value) => sum + value, 0)
  if (!(total > 0)) return null
  return values.map((value) => value / total)
}

function devigProbabilities(values, method) {
  const normalized = normalize(values)
  if (!normalized) return null
  if (method === 'multiplicative') return normalized
  if (method !== 'additive') throw new TypeError(`unsupported no-vig method: ${method}`)
  const margin = values.reduce((sum, value) => sum + value, 0) - 1
  const adjusted = values.map((value) => Math.max(0, value - margin / values.length))
  return normalize(adjusted)
}

function groupRowsByBook(rows) {
  const groups = new Map()
  for (const row of rows) {
    if (!row?.book || !row?.selection) continue
    if (!groups.has(row.book)) groups.set(row.book, new Map())
    const selections = groups.get(row.book)
    const implied = finiteProbability(row)
    if (implied === null) continue
    const current = selections.get(row.selection)
    if (!current || Number(row.american) > Number(current.row.american)) {
      selections.set(row.selection, { implied, row })
    }
  }
  return groups
}

function bookWeight(book, weighting, customWeights) {
  if (weighting === 'equal') return 1
  if (weighting !== 'market_quality') throw new TypeError(`unsupported weighting: ${weighting}`)
  return Number(customWeights?.[book] ?? MARKET_QUALITY_WEIGHTS[book] ?? 1)
}

export function buildConsensusFairValue(rows, {
  method = 'multiplicative',
  weighting = 'equal',
  bookWeights = MARKET_QUALITY_WEIGHTS,
} = {}) {
  const selections = [...new Set(rows.map((row) => row?.selection).filter(Boolean))].sort()
  const totals = Object.fromEntries(selections.map((selection) => [selection, { weighted: 0, weight: 0, books: [] }]))
  const excludedBooks = []

  const bookMarkets = [...groupRowsByBook(rows)].sort(([left], [right]) => left.localeCompare(right))
  for (const [book, bookRows] of bookMarkets) {
    if (selections.length < 2 || !selections.every((selection) => bookRows.has(selection))) {
      excludedBooks.push(book)
      continue
    }
    const probabilities = selections.map((selection) => bookRows.get(selection).implied)
    const fair = devigProbabilities(probabilities, method)
    const weight = bookWeight(book, weighting, bookWeights)
    if (!fair || !(weight > 0)) {
      excludedBooks.push(book)
      continue
    }
    selections.forEach((selection, index) => {
      totals[selection].weighted += fair[index] * weight
      totals[selection].weight += weight
      totals[selection].books.push(book)
    })
  }

  const bySelection = {}
  for (const selection of selections) {
    const sample = totals[selection]
    const probability = sample.weight > 0 ? sample.weighted / sample.weight : null
    bySelection[selection] = {
      probability,
      american: probability === null ? null : impliedToAmerican(probability),
      contributingBooks: sample.books,
      bookCount: sample.books.length,
    }
  }
  return { method, weighting, selections, bySelection, excludedBooks: excludedBooks.sort() }
}

export function expectedValuePercent(american, fairProbability) {
  const implied = americanToImplied(american)
  if (implied === null || !Number.isFinite(fairProbability)) return null
  const decimal = 1 / implied
  return 100 * (fairProbability * decimal - 1)
}
